import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FilterDropdown from '../../components/common/FilterDropdown';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import ApprovalWorkflowForm from './components/CreateFormatNumberForm';
import ViewWorkflow from './components/ViewFormatNumber';
import Alert from '../../components/Alert/Alert';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { getFormatNumbers, deleteFormatNumber, updateFormatNumber, createFormatNumber } from './api/FormatNumberApi';

const FormatNumber = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterData, setFilterData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedFormatNumber, setSelectedFormatNumber] = useState(null);
  const [formatNumbers, setFormatNumbers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000); // Auto hide after 5 seconds
  };

  useEffect(() => {
    loadFormatNumbers();
  }, []);

  const loadFormatNumbers = async () => {
    setIsLoading(true);
    try {
      const response = await getFormatNumbers();
      let data = (Array.isArray(response) ? response : response.data || []).map(item => ({
        ...item,
        name: item.name,
        departmentCode: item.departmentCode,
        moduleName: item.moduleName,
        suffixCode: item.suffixCode,
        // Perbaiki mapping field example
        example: item.exampleOutput || item.example || '', // Gunakan exampleOutput dari backend
      }));
      // Add dummy data if no data returned
      // if (!data.length) {
      //   data = [
      //     {
      //       id: 1,
      //       name: 'Invoice Number',
      //       departmentCode: 'FIN',
      //       moduleName: 'Invoice',
      //       suffixCode: 'INV',
      //       example: 'FIN-2024-07-0001-INV',
      //     },
      //     {
      //       id: 2,
      //       name: 'Purchase Order',
      //       departmentCode: 'PRC',
      //       moduleName: 'PO',
      //       suffixCode: 'PO',
      //       example: 'PRC-2024-07-0001-PO',
      //     },
      //     {
      //       id: 3,
      //       name: 'Delivery Note',
      //       departmentCode: 'LOG',
      //       moduleName: 'Delivery',
      //       suffixCode: 'DN',
      //       example: 'LOG-2024-07-0001-DN',
      //     },
      //   ];
      // }
      setFormatNumbers(data);
    } catch (error) {
      console.error('Error loading format numbers:', error);
      showAlert('danger', 'Failed to load format numbers.');
      setFormatNumbers([
        {
          id: 1,
          name: 'Invoice Number',
          departmentCode: 'FIN',
          moduleName: 'Invoice',
          suffixCode: 'INV',
          example: 'FIN/2024/07/0001-INV',
        },
        {
          id: 2,
          name: 'Purchase Order',
          departmentCode: 'PRC',
          moduleName: 'PO',
          suffixCode: 'PO',
          example: 'PRC/2024/07/0001-PO',
        },
        {
          id: 3,
          name: 'Delivery Note',
          departmentCode: 'LOG',
          moduleName: 'Delivery',
          suffixCode: 'DN',
          example: 'LOG/2024/07/0001-DN',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilterData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterReset = () => {
    setFilterData({
      dateFrom: '',
      dateTo: '',
      client: '',
      project: '',
      status: ''
    });
  };

  const handleFilterApply = () => {
    // Add your filter logic here
  };

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'departmentCode', label: 'Department Code' },
    { key: 'moduleName', label: 'Module Name' },
    { key: 'suffixCode', label: 'Suffix Code' },
    { key: 'example', label: 'Example' },
    // Action column is handled by GenericView's onEdit/onDelete/onView
  ];

  // Remove workflow-specific dummy data and logic

  const handleFormSubmit = async (formData) => {
    console.log('handleFormSubmit received:', formData);
    
    try {
      if (selectedFormatNumber) {
        // For update, include the ID
        const updatePayload = {
          ...formData,
          id: selectedFormatNumber.id
        };
        await updateFormatNumber(selectedFormatNumber.id, updatePayload);
        showAlert('success', 'Format number updated successfully!');
      } else {
        // For create, use the formData directly
        await createFormatNumber(formData);
        showAlert('success', 'Format number created successfully!');
      }
      await loadFormatNumbers();
      setIsModalOpen(false);
      setSelectedFormatNumber(null);
    } catch (error) {
      console.error('Error saving format number:', error);
      showAlert('danger', error.response?.data?.message || 'Error saving format number');
    }
  };

  // Add these new handler functions
  const handleEdit = (row) => {
    setSelectedFormatNumber(row);
    setIsModalOpen(true);
  };

  const handleDelete = async (row) => {
    setItemToDelete(row);
    setShowDeleteModal(true);
  };



  const handleView = (row) => {
    setSelectedFormatNumber(row);
    setIsViewModalOpen(true);
  };

  return (
    <div className="container-xl">
      {/* Alert Component */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <div className="row g-2 align-items-center">
        <div className="col">
          <h3 className="page-title m-0">Format Number Management</h3>
        </div>
        <div className="col-auto ms-auto d-print-none">
          <div className="btn-list">
            <button 
              className="btn btn-primary"
              onClick={() => setIsModalOpen(true)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                <path d="M12 5l0 14"></path>
                <path d="M5 12l14 0"></path>
              </svg>
              Create
            </button>
          </div>
        </div>
      </div>

      <ApprovalWorkflowForm
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedFormatNumber(null);
        }}
        onSubmit={handleFormSubmit}
        initialData={selectedFormatNumber}
      />

      <ViewWorkflow
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedFormatNumber(null);
        }}
        data={selectedFormatNumber}
      />

      {/* Confirmation Modal for Delete */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        onConfirm={async () => {
          if (!itemToDelete?.id) return;
          setIsLoading(true);
          try {
            await deleteFormatNumber(itemToDelete.id);
            showAlert('success', 'Format number deleted successfully!');
            await loadFormatNumbers();
          } catch (error) {
            console.error('Error deleting format number:', error);
            showAlert('danger', 'Failed to delete format number.');
          } finally {
            setIsLoading(false);
          }
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        title="Delete Format Number"
        message={`Are you sure you want to delete this? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                  <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-list-data" className="nav-link active" data-bs-toggle="tab" aria-selected="true" role="tab">List Data</a>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  <div className="tab-pane active show" id="tabs-list-data" role="tabpanel">
                    <GenericView
                      data={formatNumbers}
                      columns={columns}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onView={handleView}
                      isLoading={isLoading}
                      filterComponent={
                        <FilterDropdown
                          filterData={filterData}
                          onFilterChange={handleFilterChange}
                          onReset={handleFilterReset}
                          onApply={handleFilterApply}
                        />
                      }
                      searchComponent={
                        <SearchInput
                          value={searchQuery}
                          onChange={setSearchQuery}
                          placeholder="Search format numbers..."
                        />
                      }
                      actionButtons={
                        <ActionButtons
                          onExportExcel={() => {
                            showAlert('info', 'Export to Excel functionality coming soon');
                          }}
                          onExportPDF={() => {
                            showAlert('info', 'Export to PDF functionality coming soon');
                          }}
                        />
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormatNumber; 